import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, CreditCard, LifeBuoy, Loader2, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { useTenant, useSubscriptionStatus } from "../../context/TenantContext";
import { MODULE_LABELS } from "../../utils/moduleAccess";

interface SubscriptionRecord {
  id: number;
  plan_type: string;
  status: string;
  amount?: number;
  currency?: string;
  current_period_start?: string;
  current_period_end?: string;
}

interface PlanDefinition {
  key: string;
  name: string;
  cycleOptions: string[];
  priceFrom: string;
  description: string;
  modules: string[];
}

interface UsageResponse {
  users?: Record<string, number>;
  classes?: number;
  subjects?: number;
  tenant_limits?: {
    max_students?: number;
    max_teachers?: number;
    max_storage_mb?: number;
  };
}

const badgeClasses: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-100",
  trial: "bg-amber-50 text-amber-700 border-amber-100",
  expired: "bg-rose-50 text-rose-700 border-rose-100",
  suspended: "bg-slate-100 text-slate-700 border-slate-200",
  pending: "bg-blue-50 text-blue-700 border-blue-100"
};

const SchoolSubscription: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { tenant, refreshTenant } = useTenant();
  const subscriptionState = useSubscriptionStatus();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [subscription, setSubscription] = useState<SubscriptionRecord | null>(null);
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [requestedTier, setRequestedTier] = useState("paid");
  const [billingCycle, setBillingCycle] = useState("annual");
  const [notes, setNotes] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const [subscriptionRes, plansRes, usageRes] = await Promise.all([
        axios.get(getApiUrl("tenant/subscription"), { headers }),
        axios.get(getApiUrl("tenant/subscription/plans"), { headers }),
        axios.get(getApiUrl("tenant/usage"), { headers })
      ]);

      setSubscription(subscriptionRes.data?.subscription || null);
      setPlans(Array.isArray(plansRes.data?.plans) ? plansRes.data.plans : []);
      setUsage(usageRes.data || null);

      const currentPlan = String(plansRes.data?.currentPlan || tenant?.subscription_tier || "basic").toLowerCase();
      setRequestedTier(currentPlan === "trial" ? "paid" : currentPlan);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load subscription details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submitRequest = async () => {
    try {
      setSaving(true);
      setError("");
      setNotice("");
      const response = await axios.post(
        getApiUrl("tenant/subscription/request"),
        { requestedTier, billingCycle, notes },
        { headers }
      );
      setNotice(response.data?.message || "Subscription request submitted.");
      setNotes("");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to submit subscription request");
    } finally {
      setSaving(false);
    }
  };

  const currentStatus = (subscription?.status || tenant?.status || "active").toLowerCase();
  const currentBadgeClass = badgeClasses[currentStatus] || "bg-slate-50 text-slate-700 border-slate-200";
  const isExempt = Boolean(tenant?.is_subscription_exempt);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="animate-spin text-teal-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-950">Subscription & Billing</h1>
            <p className="text-slate-500 mt-2">
              Review your school&apos;s current subscription, compare available packages, and submit renewal or upgrade requests.
            </p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => { refreshTenant(); load(); }} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 inline-flex items-center gap-2">
              <RefreshCw size={16} />
              Refresh
            </button>
            <Link to="/support" className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 inline-flex items-center gap-2">
              <LifeBuoy size={16} />
              Open Support Desk
            </Link>
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-700">{error}</div> : null}
        {notice ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-semibold text-emerald-700">{notice}</div> : null}

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_420px] gap-6">
          <section className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Current Status</p>
                <div className="mt-3 flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-[0.18em] ${currentBadgeClass}`}>
                    {currentStatus}
                  </span>
                </div>
                <p className="mt-4 text-sm text-slate-600">{subscriptionState.message}</p>
                {isExempt && <p className="mt-3 text-sm font-semibold text-emerald-700">This school is exempt from subscription billing.</p>}
              </div>
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Current Plan</p>
                <p className="mt-3 text-2xl font-black text-slate-950 capitalize">{tenant?.plan_package || tenant?.subscription_tier || subscription?.plan_type || "Basic"}</p>
                <p className="mt-2 text-sm text-slate-500">
                  {subscription?.current_period_end ? `Renews or ends on ${new Date(subscription.current_period_end).toLocaleDateString()}` : "No billing date recorded yet."}
                </p>
              </div>
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Current Usage</p>
                <p className="mt-3 text-2xl font-black text-slate-950">{usage?.users?.student || 0} / {usage?.tenant_limits?.max_students || 0}</p>
                <p className="mt-2 text-sm text-slate-500">Learners currently using the school workspace.</p>
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles size={18} className="text-teal-600" />
                <h2 className="text-xl font-bold text-slate-950">Available Packages</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <article key={plan.key} className={`rounded-3xl border p-5 ${requestedTier === plan.key ? "border-teal-300 bg-teal-50/60" : "border-slate-200 bg-slate-50/40"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-950">{plan.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">{plan.description}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setRequestedTier(plan.key);
                          setBillingCycle(plan.cycleOptions.includes("annual") ? "annual" : plan.cycleOptions[0]);
                        }}
                        className={`rounded-xl px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] ${requestedTier === plan.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}
                      >
                        {requestedTier === plan.key ? "Selected" : "Choose"}
                      </button>
                    </div>
                    <p className="mt-4 text-sm font-semibold text-slate-700">From {plan.priceFrom}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {plan.cycleOptions.map((cycle) => (
                        <span key={cycle} className="rounded-full bg-white border border-slate-200 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                          {cycle}
                        </span>
                      ))}
                    </div>
                    <div className="mt-5 space-y-2">
                      {plan.modules.slice(0, 6).map((moduleKey) => (
                        <div key={moduleKey} className="text-sm text-slate-600 inline-flex items-center gap-2">
                          <ShieldCheck size={14} className="text-emerald-600" />
                          {MODULE_LABELS[moduleKey as keyof typeof MODULE_LABELS] || moduleKey}
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            {!isExempt && <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard size={18} className="text-teal-600" />
                <h2 className="text-lg font-bold text-slate-950">Request Renewal</h2>
              </div>
              <p className="text-sm text-slate-500 mb-5">
                Submit a renewal or upgrade request. The platform billing team will follow up through your support desk thread.
              </p>
              <div className="space-y-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">Requested package</span>
                  <select className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700" value={requestedTier} onChange={(event) => setRequestedTier(event.target.value)}>
                    {plans.map((plan) => <option key={plan.key} value={plan.key}>{plan.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">Billing cycle</span>
                  <select className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700" value={billingCycle} onChange={(event) => setBillingCycle(event.target.value)}>
                    {["monthly", "quarterly", "annual"].map((cycle) => <option key={cycle} value={cycle}>{cycle}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">Notes for billing</span>
                  <textarea
                    className="mt-2 min-h-[180px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 resize-y"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Add procurement notes, billing contact context, expected learner growth, or urgency."
                  />
                </label>
                <button
                  type="button"
                  disabled={saving}
                  onClick={submitRequest}
                  className="w-full rounded-2xl bg-slate-900 text-white px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  Submit Subscription Request
                </button>
              </div>
            </div>}

            {isExempt && <div className="rounded-[1.75rem] border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-emerald-900 mb-3">Subscription Exempt</h2>
              <p className="text-sm text-emerald-800">
                This school is marked as exempt from subscription billing. Renewal and upgrade actions are intentionally hidden until billing is reinstated.
              </p>
            </div>}

            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-slate-950 mb-4">Deployment Snapshot</h2>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="flex items-center justify-between"><span>Classes</span><span className="font-semibold text-slate-900">{usage?.classes || 0}</span></div>
                <div className="flex items-center justify-between"><span>Subjects</span><span className="font-semibold text-slate-900">{usage?.subjects || 0}</span></div>
                <div className="flex items-center justify-between"><span>Teachers</span><span className="font-semibold text-slate-900">{usage?.users?.teacher || 0} / {usage?.tenant_limits?.max_teachers || 0}</span></div>
                <div className="flex items-center justify-between"><span>Storage limit</span><span className="font-semibold text-slate-900">{usage?.tenant_limits?.max_storage_mb || 0} MB</span></div>
              </div>
              <p className="mt-4 text-xs text-slate-400">
                This helps the billing team recommend the right package if your school is growing beyond the current quota.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default SchoolSubscription;
